/**
 * The values the PDFs are drawn from, with no behaviour attached.
 *
 * NOT A SHARED STYLESHEET, deliberately. `Font.register` is keyed globally by
 * family name, so a shared registration module would change font resolution
 * for two shipped PDFs that have no unit tests and, in the career plan's case,
 * a byte-level contract — `plan-pdf-export` hashes its output and asserts the
 * same plan renders identically twice.
 *
 * So each document keeps its own `Font.register` and its own
 * `StyleSheet.create`, and only the constants live here. That makes this the
 * seam for a later extraction without touching anything that currently ships.
 */

/** Bold is a separate FAMILY in this codebase, never a `fontWeight`. */
export const PDF_FONT = {
  regular: "ProximaNova",
  bold: "ProximaNovaBold",
  /** A separate family, like bold: @react-pdf has no synthetic italic. */
  italic: "ProximaNovaItalic",
  /** The signature, in the face the sign dialog uses. */
  signature: "MrDafoe",
} as const;

export const PDF_COLOR = {
  text: "#1c252e",
  muted: "#637381",
  border: "#dfe3e8",
  surface: "#f4f6f8",
  accent: "#ffd100",
  blue: "#078dee",
  green: "#118d57",
  greenSurface: "#edf8f1",
  purple: "#8e33ff",
  /** `purple` at 8% on white - the Top Hat's FIT callout. */
  purpleSurface: "#f6eeff",
  amberSurface: "#fff8eb",
  amberText: "#7a4100",
  /** The Program Spotlight's border on screen. */
  accentSoft: "#ffd666",
  /** The rating glyphs, from client-common's sentiment palette. */
  love: "#ea1d76",
  like: "#078dee",
  white: "#ffffff",

  /**
   * The chips and tinted rows the Climb Plan screen draws, flattened.
   *
   * @react-pdf has no alpha compositing on a background colour, so the app's
   * `rgba(x, y, z, 0.08)` tints are pre-blended against white here. Taken from
   * SkillCard, KeyToSuccessCard and ProgramDetailBody so a printed plan wears
   * the same colours as the screen it came from.
   */
  strengthChipSurface: "#efd6ff",
  strengthChipText: "#5119b7",
  goalChip: "#22c55e",
  /** The CONSIDERING badge on an After High School path card. */
  consideringChipSurface: "#ffe7af",
  consideringChipText: "#b76e00",
  blueSurface: "#e7f3fe",
  blueSurfaceStrong: "#dceefc",
  amberRowSurface: "#fff6e5",
} as const;

/** The yellow rule across the top of every page. */
export const PDF_ACCENT_BAR_HEIGHT = 6;

/**
 * Room reserved for the repeating header and footer.
 *
 * A `fixed` element repeats on every page but does NOT push the flowing
 * content down on the pages after the first — so the page's own padding is
 * what keeps page two from starting underneath the logo.
 */
export const PDF_HEADER_HEIGHT = 46;
export const PDF_FOOTER_HEIGHT = 28;

export const PDF_TYPE = {
  sectionTitle: 9,
  heading: 13,
  subheading: 11,
  body: 9,
  small: 8,
} as const;
